import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import {
  Button, Card, Spinner, Alert, AlertDescription, Badge,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  SearchInput, EmptyState,
} from '@agion/ui'
import { Box, RefreshCw } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { formatDate } from '@/core/infra_utils'
import {
  listChartVersions, listDeployedCharts, listChartDeviations, refreshChartDeviations,
  type ChartVersion, type DeployedChart, type ChartDeviation,
} from './infra_chart.service'
import { ChartDeviationsBanner } from './InfraChartDeviationsBanner'

type ViewMode = 'versions' | 'deployed'

export function ChartsPage() {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const viewMode: ViewMode = searchParams.get('view') === 'deployed' ? 'deployed' : 'versions'
  const setViewMode = (mode: ViewMode) => {
    setSearchParams(mode === 'deployed' ? { view: 'deployed' } : {}, { replace: true })
  }
  const [versions, setVersions] = useState<ChartVersion[]>([])
  const [deployed, setDeployed] = useState<DeployedChart[]>([])
  const [deviations, setDeviations] = useState<ChartDeviation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const [versionsResp, deployedResp, deviationsResp] = await Promise.all([
        listChartVersions().catch(() => [] as ChartVersion[]),
        listDeployedCharts().catch(() => [] as DeployedChart[]),
        listChartDeviations().catch(() => [] as ChartDeviation[]),
      ])
      setVersions(versionsResp ?? [])
      setDeployed(deployedResp ?? [])
      setDeviations(deviationsResp ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chart data')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const handleRefreshDeviations = async () => {
    setIsRefreshing(true)
    try {
      await refreshChartDeviations()
      await loadData()
    } finally {
      setIsRefreshing(false)
    }
  }

  const filteredVersions = versions.filter((v) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return v.version.toLowerCase().includes(q) || v.appVersion.toLowerCase().includes(q)
  })

  const filteredDeployed = deployed.filter((d) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      d.environmentName.toLowerCase().includes(q) ||
      d.releaseName.toLowerCase().includes(q) ||
      d.chartVersion.toLowerCase().includes(q)
    )
  })

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('pages:charts.title', 'Helm Charts')}
        description={t('pages:charts.description', 'Manage Edge Helm chart versions and deployments')}
        icon={Box}
        actions={
          <Button variant="outline" size="sm" onClick={() => void loadData()}>
            <RefreshCw size={14} className="mr-1" />
            {t('pages:charts.refresh', 'Refresh')}
          </Button>
        }
      />

      <ChartDeviationsBanner
        deviations={deviations}
        onRefresh={() => void handleRefreshDeviations()}
        isRefreshing={isRefreshing}
      />

      <div className="flex items-center gap-4">
        <div className="max-w-md flex-1">
          <SearchInput
            placeholder={t('pages:charts.search', 'Search charts...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />
        </div>
        <div className="flex gap-1 ml-auto">
          <Button
            variant={viewMode === 'versions' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setViewMode('versions')}
          >
            <Box size={14} className="mr-1" />
            {t('pages:charts.views.versions', 'Versions')}
          </Button>
          <Button
            variant={viewMode === 'deployed' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setViewMode('deployed')}
          >
            <Box size={14} className="mr-1" />
            {t('pages:charts.views.deployed', 'Deployed')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : viewMode === 'versions' ? (
        <VersionsView items={filteredVersions} />
      ) : (
        <DeployedView items={filteredDeployed} deviations={deviations} navigate={navigate} />
      )}
    </div>
  )
}

function VersionsView({ items }: { items: ChartVersion[] }) {
  const { t } = useTranslation(['pages'])

  if (items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Box}
          title={t('pages:charts.empty.title', 'No chart versions found')}
          description={t('pages:charts.empty.description', 'No Helm chart versions found in the repository.')}
        />
      </Card>
    )
  }

  return (
    <Table variant="modern">
      <TableHeader variant="modern">
        <TableRow variant="modern">
          <TableHead variant="modern">{t('pages:charts.columns.version', 'Chart Version')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.appVersion', 'App Version')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.description', 'Description')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.created', 'Published')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.digest', 'Digest')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody variant="modern">
        {items.map((v, idx) => (
          <TableRow key={v.version} variant="modern">
            <TableCell className="font-mono font-medium">
              {v.version}
              {idx === 0 && (
                <Badge variant="success" className="ml-2">{t('pages:charts.latest')}</Badge>
              )}
            </TableCell>
            <TableCell className="font-mono">{v.appVersion}</TableCell>
            <TableCell className="text-slate-500 max-w-xs truncate">{v.description}</TableCell>
            <TableCell className="text-slate-500">{formatDate(v.created)}</TableCell>
            <TableCell className="font-mono text-xs text-slate-400 max-w-[120px] truncate">
              {v.digest ? v.digest.substring(0, 16) + '...' : '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function DeployedView({
  items,
  deviations,
  navigate,
}: {
  items: DeployedChart[]
  deviations: ChartDeviation[]
  navigate: (path: string) => void
}) {
  const { t } = useTranslation(['pages'])

  if (items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Box}
          title={t('pages:charts.deployed.empty.title', 'No deployments found')}
          description={t('pages:charts.deployed.empty.description', 'No Helm chart deployments found across environments.')}
        />
      </Card>
    )
  }

  const getDeviation = (envId: string) =>
    deviations.find(d => d.environmentId === envId)

  return (
    <Table variant="modern">
      <TableHeader variant="modern">
        <TableRow variant="modern">
          <TableHead variant="modern">{t('pages:charts.columns.environment', 'Environment')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.release', 'Release')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.chartVersion', 'Chart Version')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.status', 'Status')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.revision', 'Revision')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.updated', 'Updated')}</TableHead>
          <TableHead variant="modern">{t('pages:charts.columns.actions', 'Actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody variant="modern">
        {items.map((d) => {
          const deviation = getDeviation(d.environmentId)
          return (
            <TableRow key={d.id} variant="modern">
              <TableCell className="font-medium">{d.environmentName}</TableCell>
              <TableCell className="font-mono text-sm">{d.releaseName}</TableCell>
              <TableCell>
                <span className="font-mono">{d.chartVersion}</span>
                {deviation && (
                  <Badge
                    variant={deviation.deviationType === 'outdated' ? 'warning' : 'destructive'}
                    className="ml-2"
                  >
                    {deviation.deviationType === 'outdated'
                      ? t('pages:charts.deviation.outdated', 'Outdated')
                      : t('pages:charts.deviation.unknown', 'Unknown')}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={d.status === 'deployed' ? 'success' : 'default'}>
                  {d.status}
                </Badge>
              </TableCell>
              <TableCell className="text-slate-500">{d.revision}</TableCell>
              <TableCell className="text-slate-500">{formatDate(d.updatedAt)}</TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {deviation && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/environments/${d.environmentId}?action=install-edge`)}
                    >
                      {t('pages:charts.actions.upgrade', 'Upgrade')}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/environments/${d.environmentId}`)}
                  >
                    {t('pages:charts.actions.details', 'Details')}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
